import {yupResolver} from "@hookform/resolvers/yup";
import {BraintreeError} from "braintree-web/core";
import {HostedFieldsTokenizePayload} from "braintree-web/hosted-fields";
import {PayPalTokenizePayload} from "braintree-web/paypal";
import classnames from "classnames";
import pick from "lodash/pick";
import {ReactElement, useCallback, useEffect, useMemo, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {AnyObjectSchema} from "yup";

import AmexLogo from "@/assets/images/billing/payment-method-icons/amex.svg";
import MastercardLogo from "@/assets/images/billing/payment-method-icons/mastercard.svg";
import PayPalLogo from "@/assets/images/billing/payment-method-icons/paypal.svg";
import VisaLogo from "@/assets/images/billing/payment-method-icons/visa.svg";
import {useCheckoutContext} from "@/components/context/hooks.ts";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import RadioTabs from "@/components/form/RadioTabs.tsx";
import {BillingData, PaymentMethodType} from "@/lib/types/billing.ts";
import {ServerErrors} from "@/lib/types/common.ts";
import {PaymentMethodCreditCardForm} from "@/pages/billing/payment-method/PaymentMethodCreditCardForm.tsx";
import {PaymentMethodPaypalForm} from "@/pages/billing/payment-method/PaymentMethodPaypalForm.tsx";
import {CreditCardFormSchema, PaypalFormSchema} from "@/pages/billing/payment-method/schemas.ts";

import css from "./PaymentMethodForm.module.scss";

export interface PaymentMethodFormProps {
  braintreeToken: string;
  billingData: BillingData;
  onSave: (formValues: PaymentMethodFormValues, nonce: string, paymentMethodType: PaymentMethodType) => void;
  formId: string;
  serverErrors: ServerErrors;
  onTabChange?: (tab: PaymentMethodType) => void;
  variant?: "default" | "narrow";
  beforeFormContent?: ReactElement;
  extraScheme?: AnyObjectSchema;
  hasRequiredFields?: boolean;
}

export interface PaymentMethodFormValues {
  cardholder_name?: string | null | undefined;
  country?: string;
  zipcode?: string;
}

const tabTitles = [
  {
    id: PaymentMethodType.CREDIT_CARD,
    title: (
      <>
        <img className="me-2" src={VisaLogo} alt="Visa" />
        <img className="me-2" src={MastercardLogo} alt="MasterCard" />
        <img className="img-line-height" src={AmexLogo} alt="American Express" />
      </>
    ),
  },
  {
    id: PaymentMethodType.PAYPAL,
    title: (
      <>
        <img className="me-2" src={PayPalLogo} alt="PayPal" />
        <strong>PayPal</strong>
      </>
    ),
  },
];

export const PaymentMethodForm = ({
  billingData,
  braintreeToken,
  onSave,
  formId,
  variant,
  serverErrors,
  onTabChange,
  extraScheme,
  beforeFormContent,
  hasRequiredFields = true,
}: PaymentMethodFormProps) => {
  const [currentTab, setCurrentTab] = useState(PaymentMethodType.CREDIT_CARD);
  const [btTokenizeFn, setBtTokenizeFn] = useState<
    () => (
      options: object,
      callback: (err: BraintreeError, payload?: HostedFieldsTokenizePayload | PayPalTokenizePayload) => void
    ) => void
  >(() => () => {});
  let validationScheme = currentTab === PaymentMethodType.CREDIT_CARD ? CreditCardFormSchema : PaypalFormSchema;
  if (extraScheme) {
    // TODO: Alexei: fix proper schemas typing
    // @ts-expect-error incomplete schema
    validationScheme = extraScheme.concat(validationScheme);
  }
  const formResolver = useCallback(() => yupResolver<PaymentMethodFormValues>(validationScheme), [validationScheme]);
  const defaultBillingValues = useMemo<BillingData>(
    () =>
      Object.fromEntries(
        Object.entries(billingData).map(([key, value]) => [key, value === null ? "" : value])
      ) as BillingData,
    [billingData]
  );
  const [btErrors, setBtErrors] = useState<string[]>([]);
  const methods = useForm<PaymentMethodFormValues>({
    resolver: formResolver(),
    defaultValues: defaultBillingValues,
  });
  const {reset, handleSubmit, setError} = methods;

  const [hostedFieldsErrors, setHostedFieldsErrors] = useState<Record<string, string>>({});
  const {setRequestInProgress} = useCheckoutContext();
  useEffect(() => {
    // Loading billing data from the backend takes time, so the customer
    // might have already started filling out the form. We want to reset
    // only the fields that are not dirty.
    if (!serverErrors) {
      reset(defaultBillingValues, {keepDirtyValues: true});
    }
  }, [defaultBillingValues, reset]);
  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors).forEach((key) => {
        setError(key as keyof PaymentMethodFormValues, {message: serverErrors[key][0]});
      });
    }
  }, [setError, serverErrors]);
  return (
    <FormProvider {...methods}>
      <div id="payment-processor-errors">
        {(serverErrors?.__all__?.length > 0 || btErrors.length > 0) && (
          <div className="mb-3">
            <FormErrorAlerts errors={(btErrors.length > 0 && btErrors) || serverErrors?.__all__ || []} />
          </div>
        )}
      </div>
      <form
        id={formId}
        onSubmit={handleSubmit(
          (data) => {
            // PLACE WHERE IT STARTS
            setRequestInProgress(true);
            setHostedFieldsErrors({});
            setBtErrors([]);
            const btOptions =
              currentTab === PaymentMethodType.PAYPAL
                ? {
                    flow: "vault",
                    billingAgreementDescription: "Uptime.com subscription",
                    singleUse: false,
                    locale: "en_US",
                    enableShippingAddress: false,
                  }
                : {
                    cardholderName: data.cardholder_name,
                  };
            const tokenizeFn = btTokenizeFn();
            tokenizeFn(btOptions, function (tokenizeErr, payload) {
              if (tokenizeErr || !payload?.nonce) {
                setRequestInProgress(false);
                const btFieldErrors = {
                  number: "Invalid card number.",
                  cvv: "Invalid CVV number.",
                  expirationDate: "Invalid expiration date.",
                };
                const hostedFieldsContainer = document.getElementById("hosted-fields-container");
                switch (tokenizeErr.code) {
                  case "HOSTED_FIELDS_FIELDS_EMPTY":
                    setHostedFieldsErrors(btFieldErrors);
                    hostedFieldsContainer?.scrollIntoView({block: "end"});
                    break;
                  case "HOSTED_FIELDS_FIELDS_INVALID":
                    setHostedFieldsErrors(pick(btFieldErrors, tokenizeErr.details?.invalidFieldKeys || []));
                    hostedFieldsContainer?.scrollIntoView({block: "end"});
                    break;
                  default:
                    // generic error, could be coming from anywhere.
                    setBtErrors(["Our payment provider reported an error.", tokenizeErr.message]);
                }
              } else {
                onSave(data, payload.nonce, currentTab as PaymentMethodType);
              }
            });
          },
          () => {
            setHostedFieldsErrors({});
          }
        )}
      >
        {beforeFormContent}
        <RadioTabs
          className="border-0 mt-2 mb-4"
          items={tabTitles}
          selectedId={currentTab}
          selectTab={(tab) => {
            setCurrentTab(tab as PaymentMethodType);
            if (onTabChange) {
              onTabChange(tab as PaymentMethodType);
            }
          }}
        />
        <div className={classnames(css.PaymentMethodForm)} id="hosted-fields-container">
          {currentTab === PaymentMethodType.CREDIT_CARD && (
            <PaymentMethodCreditCardForm
              braintreeToken={braintreeToken}
              variant={variant}
              hasRequiredFields={hasRequiredFields}
              onReady={(tokenizeFn) => setBtTokenizeFn(() => tokenizeFn)}
              hostedFieldErrors={hostedFieldsErrors}
              onError={(errors) => setBtErrors(errors)}
            />
          )}
          {currentTab === PaymentMethodType.PAYPAL && (
            <PaymentMethodPaypalForm
              onReady={(tokenizeFn) => setBtTokenizeFn(() => tokenizeFn)}
              braintreeToken={braintreeToken}
              onError={(errors) => setBtErrors(errors)}
            />
          )}
        </div>
      </form>
    </FormProvider>
  );
};
